<script>
import { mapGetters, mapActions } from 'vuex';
import DocumentsTitleUpload from '@/V2/documents/DocumentsTitleUpload';
import CustomerFraudUploading from '@/V2/customers/fraud/CustomerFraudUploading.vue';
import { hasAgentRunInProgress } from '@/utils/ai-agents';
import CustomerComplianceSummary from '@/V2/customers/business-compliance-agent/CustomerComplianceSummary.vue';
import CustomerComplianceDistribution from '@/V2/customers/business-compliance-agent/CustomerComplianceDistribution.vue';
import CustomerComplianceBusinessChecks from '@/V2/customers/business-compliance-agent/CustomerComplianceBusinessChecks.vue';
import CustomerComplianceIndividualChecks from '@/V2/customers/business-compliance-agent/CustomerComplianceIndividualChecks.vue';
import CustomerComplianceCheckDetails from '@/V2/customers/business-compliance-agent/CustomerComplianceCheckDetails.vue';
import UpsellScreenCompliance from '@/V2/components/upsell/UpsellScreenCompliance.vue';
import { RUN_STATUS } from '@/constants/analyst-constants';
import { getLatestBusinessComplianceAgentRun } from '@/V2/customers/business-compliance-agent/utils';

export default {
  components: {
    DocumentsTitleUpload,
    CustomerComplianceSummary,
    CustomerFraudUploading,
    CustomerComplianceDistribution,
    CustomerComplianceBusinessChecks,
    CustomerComplianceIndividualChecks,
    CustomerComplianceCheckDetails,
    UpsellScreenCompliance,
  },
  data() {
    return {
      showDocumentsUploadingComponent: false,
      currentVisibleCheck: null,
    };
  },
  computed: {
    ...mapGetters([
      'canViewBusinessComplianceAgent',
      'getCachedDocumentReviewAgentRunsForCustomerUUID',
      'someDocumentUploadingOrProcessing',
      'getCachedBusinessComplianceAgentRunsForCustomerUUID',
    ]),
    complianceIsRunning() {
      return [RUN_STATUS.RUNNING, RUN_STATUS.PENDING].includes(
        this.latestComplianceRun?.status,
      );
    },
    complianceSummary() {
      const summary = {
        aggregated_result_counts: {},
      };
      const beneficialOwnerChecks =
        this.latestComplianceRun?.output?.beneficial_owner_checks?.flatMap(
          (beneficialOwnerCheck) => beneficialOwnerCheck.checks || [],
        ) || [];

      const allChecks = [
        ...(this.latestComplianceRun?.output?.company_checks || []),
        ...beneficialOwnerChecks,
      ];

      allChecks.forEach((check) => {
        if (!summary.aggregated_result_counts[check.result]) {
          summary.aggregated_result_counts[check.result] = 0;
        }
        summary.aggregated_result_counts[check.result]++;
      });
      return summary;
    },
    latestComplianceRun() {
      const runs = this.getCachedBusinessComplianceAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      if (typeof runs === 'undefined') {
        return undefined;
      }
      return getLatestBusinessComplianceAgentRun(runs);
    },
    isFetchingData() {
      return typeof this.latestComplianceRun === 'undefined';
    },
    complianceStatus() {
      return this.someDocumentUploadingOrProcessing ||
        this.isDocumentReviewAgentRunning ||
        this.isComplianceAgentRunning
        ? RUN_STATUS.RUNNING
        : this.latestComplianceRun?.status;
    },
    isDocumentReviewAgentRunning() {
      const runs = this.getCachedDocumentReviewAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return hasAgentRunInProgress(runs);
    },
    isComplianceAgentRunning() {
      const runs = this.getCachedBusinessComplianceAgentRunsForCustomerUUID(
        this.$route.params.customer_id,
      );
      return hasAgentRunInProgress(runs);
    },
    isComplianceRunning() {
      return this.complianceStatus === RUN_STATUS.RUNNING;
    },
  },
  methods: {
    onViewReportButtonClick() {
      this.$router.push({
        name: 'customer-compliance-report',
        params: { customer_id: this.$route.params.customer_id },
      });
    },
    showCheckDetails(check) {
      this.currentVisibleCheck = check;
    },
    onDrawerClose() {
      this.currentVisibleCheck = null;
    },
  },
};
</script>

<template>
  <div
    class="customer-compliance i-px-11 i-pt-11 i-pb-11 i-flex"
    data-testid="customer-compliance-page"
  >
    <upsell-screen-compliance
      v-if="!canViewBusinessComplianceAgent"
    ></upsell-screen-compliance>
    <div v-else class="customer-compliance-container d-flex flex-column">
      <!-- title with upload -->
      <documents-title-upload
        ref="documentsTitleUpload"
        title="Compliance"
        subtitle="View the compliance analysis for this customer"
        :expanded="false"
        @files-selected-for-upload="showDocumentsUploadingComponent = true"
      >
      </documents-title-upload>

      <!-- content -->
      <div class="compliance-content d-flex flex-column i-gap-8">
        <customer-fraud-uploading v-if="showDocumentsUploadingComponent" />

        <!-- summaries -->
        <div class="d-flex flex-row i-gap-8">
          <!-- compliance summary -->
          <customer-compliance-summary
            class="summary-card half-width"
            title="Analysis"
            button-text="View Report"
            :latest-compliance-run="latestComplianceRun"
            :loading="isFetchingData"
            :is-compliance-running="isComplianceRunning"
            @button-click="onViewReportButtonClick"
          />

          <customer-compliance-distribution
            class="half-width"
            :loading="isFetchingData"
            :is-compliance-running="isComplianceRunning"
            :summary="complianceSummary"
          />
        </div>

        <!-- business checks -->
        <customer-compliance-business-checks
          :loading="isFetchingData"
          :latest-compliance-run="latestComplianceRun"
          :compliance-status="complianceStatus"
          :is-compliance-running="isComplianceRunning"
          @show-check-details="showCheckDetails"
        />

        <!-- individual checks -->
        <customer-compliance-individual-checks
          :loading="isFetchingData"
          :latest-compliance-run="latestComplianceRun"
          :is-compliance-running="isComplianceRunning"
          @show-check-details="showCheckDetails"
        />
      </div>

      <!-- details drawer -->
      <customer-compliance-check-details
        :open="showCheckDetails"
        :check="currentVisibleCheck"
        @close="onDrawerClose"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.customer-compliance {
  overflow-y: auto;

  .customer-compliance-container {
    max-width: 1200px;
    margin: 0 auto;
  }

  .half-width {
    width: 50%;

    :deep(.expandable-container),
    :deep(.content) {
      height: 100% !important;
    }
  }
}
</style>
